import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api/client";
import { toast } from "sonner";
import { BrandMark, BRAND } from "../components/Brand";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { ArrowLeft, Plus, PencilSimple, Trash, Tag, ArrowClockwise } from "@phosphor-icons/react";

export default function ObjectTypesSetup() {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // {id, name} or null for new
  const [nameInput, setNameInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/object-types", { params: { include_inactive: true } });
      setTypes(data.object_types || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load object types");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const startAdd = () => { setEditing(null); setNameInput(""); setEditorOpen(true); };
  const startEdit = (t) => { setEditing(t); setNameInput(t.name); setEditorOpen(true); };

  const submit = async () => {
    const name = nameInput.trim();
    if (!name) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/object-types/${editing.id}`, { name });
        toast.success("Object type updated");
      } else {
        await api.post(`/object-types`, { name });
        toast.success("Object type added");
      }
      setEditorOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (t) => {
    try {
      await api.patch(`/object-types/${t.id}`, { name: t.name, active: !t.active });
      toast.success(t.active ? "Deactivated" : "Activated");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Tasks referencing it will still keep the value but it won't appear in dropdowns.`)) return;
    try {
      await api.delete(`/object-types/${t.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed. Consider deactivating instead."); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="object-types-setup-page">
      <header className="border-b border-neutral-200 bg-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/setup" className="text-neutral-500 hover:text-neutral-900 flex items-center gap-1.5 text-sm" data-testid="back-to-setup">
            <ArrowLeft size={14} /> Back to setup
          </Link>
          <div className="h-5 w-px bg-neutral-200" />
          <BrandMark size={26} />
          <div className="ml-2">
            <h1 className="text-lg font-black tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>Object Types</h1>
            <p className="text-xs text-neutral-500">Manage the dropdown values shown on the task Object Type field.</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} className="h-9" data-testid="reload-types-btn">
              <ArrowClockwise size={13} weight="bold" className="mr-1.5" /> Reload
            </Button>
            <Button size="sm" className="h-9 text-white" style={{ backgroundColor: BRAND.colors.blue }}
                    onClick={startAdd} data-testid="add-type-btn">
              <Plus size={13} weight="bold" className="mr-1.5" /> Add object type
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto p-6">
        {loading ? (
          <div className="text-sm text-neutral-500 py-8">Loading…</div>
        ) : types.length === 0 ? (
          <div className="text-center py-16 text-neutral-500 text-sm">No object types yet. Add one to get started.</div>
        ) : (
          <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden" data-testid="types-list">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 border-b border-neutral-200 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                <tr>
                  <th className="text-left px-4 py-2.5">Name</th>
                  <th className="text-left px-4 py-2.5 w-32">Status</th>
                  <th className="text-left px-4 py-2.5 w-40">Created</th>
                  <th className="w-32"></th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => (
                  <tr key={t.id} className="border-b border-neutral-100 last:border-b-0 hover:bg-neutral-50" data-testid={`type-row-${t.id}`}>
                    <td className="px-4 py-3">
                      <div className="inline-flex items-center gap-2 text-sm font-medium text-neutral-900">
                        <Tag size={13} weight="bold" className="text-neutral-400" />
                        {t.name}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => toggleActive(t)}
                        data-testid={`toggle-active-${t.id}`}
                        className={`h-6 px-2 rounded-full text-[10px] uppercase tracking-widest font-semibold ${
                          t.active ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                                   : "bg-neutral-100 text-neutral-500 border border-neutral-200"
                        }`}
                      >
                        {t.active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500 font-mono">{(t.created_at || "").slice(0, 10)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(t)} className="h-8 w-8 p-0" data-testid={`edit-type-${t.id}`}>
                        <PencilSimple size={13} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(t)}
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-700" data-testid={`delete-type-${t.id}`}>
                        <Trash size={13} />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-w-sm" data-testid="type-editor-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Rename object type" : "Add object type"}</DialogTitle>
          </DialogHeader>
          <div>
            <Input
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
              placeholder="e.g. Ticket, Screen, Report"
              className="h-10"
              data-testid="type-name-input"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditorOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving}
                    className="text-white" style={{ backgroundColor: BRAND.colors.blue }}
                    data-testid="save-type-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
